import { Icon } from '@iconify-icon/react';

import DemoForm from './DemoForm';

/* ── Hero ─────────────────────────────────────────────
   Editorial split layout. Big serif headline on the left,
   form floating on the right with a soft shadow halo.
   The eyebrow + headline are designed to read like a
   magazine cover, not a SaaS hero. ──────────────────── */

const valueChips = [
  { label: 'Live build', icon: 'solar:tuning-square-bold' },
  { label: 'Your real docs', icon: 'solar:document-text-bold' },
  { label: 'Cited answers', icon: 'solar:link-circle-bold' },
];

const DemoHero: React.FC = () => {
  return (
    <section
      id="book-demo"
      className="relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse 90% 60% at 20% 0%, #eef4fb 0%, transparent 50%), ' +
          'radial-gradient(ellipse 70% 50% at 100% 30%, #faf0e0 0%, transparent 55%), ' +
          '#fdfcfa',
      }}>
      {/* Dot grid overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.4]"
        style={{
          backgroundImage: 'radial-gradient(rgba(13, 27, 62, 0.12) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          maskImage: 'radial-gradient(ellipse 90% 80% at 50% 30%, black 30%, transparent 90%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 90% 80% at 50% 30%, black 30%, transparent 90%)',
        }}
      />

      {/* Single hairline grid divider running vertically (editorial cue) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-px -translate-x-1/2 lg:block"
        style={{
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(13, 27, 62, 0.08) 20%, rgba(13, 27, 62, 0.08) 80%, transparent 100%)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-6 sm:pt-20 lg:px-8 lg:pb-24 lg:pt-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* ── Left: editorial copy ── */}
          <div className="lg:col-span-7 lg:pr-6">
            {/* Eyebrow */}
            <div className="hero-fade-in flex items-center gap-3">
              <span className="inline-block h-px w-8 bg-[#0a1024]/40" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#0a1024]/60">
                Live Demo · 25 minutes
              </span>
            </div>

            {/* Headline — Fraunces with mixed weights & one italic word for editorial flair */}
            <h1
              className="hero-fade-in mt-6 text-[44px] leading-[0.98] tracking-[-0.02em] text-[#0a1024] sm:text-[60px] lg:text-[78px]"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 400 }}>
              See your{' '}
              <span
                style={{
                  fontFamily: 'Fraunces, serif',
                  fontWeight: 500,
                  fontStyle: 'italic',
                  fontVariationSettings: '"SOFT" 100, "opsz" 144',
                  color: '#004790',
                }}>
                knowledge
              </span>
              <br />
              become an{' '}
              <span
                style={{
                  fontFamily: 'Fraunces, serif',
                  fontWeight: 500,
                  fontStyle: 'italic',
                  fontVariationSettings: '"SOFT" 100, "opsz" 144',
                  color: '#c08838',
                }}>
                answer.
              </span>
            </h1>

            {/* Lede */}
            <p className="hero-fade-in mt-7 max-w-[44ch] text-[17px] leading-[1.55] text-slate-600 sm:text-[18px]">
              No slides. No talking heads. Bring three documents you already have — a policy, a
              contract, a runbook — and we&apos;ll build a working agent on them while you watch.
              You&apos;ll leave with the agent.
            </p>

            {/* Value chips */}
            <div className="hero-fade-in mt-8 flex flex-wrap items-center gap-2">
              {valueChips.map((chip) => (
                <span
                  key={chip.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#0a1024]/10 bg-white/70 px-3.5 py-1.5 text-[12px] font-semibold text-[#0a1024]/75 backdrop-blur-sm">
                  <Icon icon={chip.icon} className="text-[14px] text-[#004790]" />
                  {chip.label}
                </span>
              ))}
            </div>

            {/* Anchored guarantee row */}
            <div className="hero-fade-in mt-10 grid max-w-md grid-cols-3 divide-x divide-[#0a1024]/10">
              {[
                { value: 'Live', label: 'demo' },
                { value: '0', label: 'slides' },
                { value: '∞', label: 'questions' },
              ].map((s) => (
                <div key={s.label} className="px-4 first:pl-0">
                  <div
                    className="text-[28px] leading-none text-[#0a1024]"
                    style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}>
                    {s.value}
                  </div>
                  <div className="mt-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Scroll cue */}
            <div className="hero-fade-in mt-12 hidden items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 lg:flex">
              <span className="inline-block h-px w-6 bg-slate-300" />
              Or read on — what you&apos;ll actually see ↓
            </div>
          </div>

          {/* ── Right: form ── */}
          <div className="hero-form-in lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <DemoForm variant="hero" />
              {/* Reassurance footnote */}
              <div className="mt-6 flex items-center gap-3 text-[12px] text-slate-500">
                <Icon icon="solar:shield-check-bold" className="text-[18px] text-emerald-600" />
                <span>
                  ISO/IEC 27001:2022 certified · SOC 2 Type II audit in progress · GDPR &amp; CCPA
                  aligned. Your docs never train a foundation model — ever.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Entrance animations */}
      <style jsx>{`
        .hero-fade-in {
          opacity: 0;
          transform: translateY(14px);
          animation: heroFade 0.9s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .hero-fade-in:nth-child(1) {
          animation-delay: 0.05s;
        }
        .hero-fade-in:nth-child(2) {
          animation-delay: 0.15s;
        }
        .hero-fade-in:nth-child(3) {
          animation-delay: 0.25s;
        }
        .hero-fade-in:nth-child(4) {
          animation-delay: 0.35s;
        }
        .hero-fade-in:nth-child(5) {
          animation-delay: 0.45s;
        }
        .hero-fade-in:nth-child(6) {
          animation-delay: 0.55s;
        }
        .hero-form-in {
          opacity: 0;
          transform: translateY(20px);
          animation: heroFade 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          animation-delay: 0.3s;
        }
        @keyframes heroFade {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default DemoHero;
